import React, { useState, useEffect } from "react";

import { motion } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import proxyApiService from "../services/proxy-api";

const SeriesCategoryView = () => {
  const { playlistId, categoryName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Playlist & Profile State
  const [playlist, setPlaylist] = useState(null);
  const [profile, setProfile] = useState({});
  const [domain, setDomain] = useState("");

  // State
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingFavorites, setProcessingFavorites] = useState(new Set());
  const [favorites, setFavorites] = useState([]);

  // Pagination & Display
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Sorting & Search
  const [sortBy, setSortBy] = useState("dateAdded");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState(null);

  // Grid size (1 = large, 2 = medium, 3 = small, 4 = extra small)
  const [gridSize, setGridSize] = useState(4);

  // Page jump modal
  const [showPageJump, setShowPageJump] = useState(false);
  const [pageJumpValue, setPageJumpValue] = useState("");

  // Fetch series
  useEffect(() => {
    fetchSeries();
  }, [playlistId, categoryName, currentPage, itemsPerPage, sortBy, sortOrder]);

  // Fetch favorites
  useEffect(() => {
    fetchFavorites();
  }, [playlistId]);

  // Fetch playlist and profile data
  useEffect(() => {
    const fetchPlaylistData = async () => {
      try {
        const playlistData = await apiService.getPlaylist(playlistId);

        setPlaylist(playlistData);
        setDomain(playlistData.domain || "");
      } catch (error) {
        console.error("Failed to fetch playlist:", error);
      }
    };

    const loadProfile = () => {
      const storedProfile = sessionStorage.getItem(`profile_${playlistId}`);

      if (storedProfile) {
        setProfile(JSON.parse(storedProfile));
      }
    };

    fetchPlaylistData();
    loadProfile();
  }, [playlistId]);

  const fetchSeries = async () => {
    setLoading(true);
    try {
      const decodedCategoryName = decodeURIComponent(categoryName);

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        ...(searchQuery && { search: searchQuery }),
        // Only add categories parameter if not "All"
        ...(decodedCategoryName !== "All" && {
          categories: decodedCategoryName,
        }),
      };

      const response = await proxyApiService.getSeries(playlistId, params);

      if (response?.data) {
        const seriesWithType = response.data.map((movie) => ({
          ...movie,
          mediaType: "tv",
        }));

        setSeries(seriesWithType);
        setTotalItems(response.meta?.totalItems || 0);
        setTotalPages(response.meta?.totalPages || 1);
      }
    } catch (error) {
      console.error("Failed to fetch series:", error);
      toast.error("Failed to load series");
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await proxyApiService.proxyRequest(
        playlistId,
        "/bookmarks"
      );

      if (response?.data) {
        const allFavorites = [
          ...(response.data.liveTv || []),
          ...(response.data.series || []),
          ...(response.data.series || []),
        ];

        setFavorites(allFavorites);
      }
    } catch (error) {
      console.error("Failed to fetch favorites:", error);
    }
  };

  const isFavorite = (mediaId) =>
    favorites.some((f) => String(f.id || f.mediaId) === String(mediaId));

  const handleToggleFavorite = async (movie) => {
    const mediaId = movie.mediaId || movie.id;

    if (processingFavorites.has(mediaId)) return;

    setProcessingFavorites((prev) => new Set([...prev, mediaId]));

    try {
      const wasFavorite = isFavorite(mediaId);

      if (wasFavorite) {
        await proxyApiService.deleteBookmark(playlistId, String(mediaId), "tv");
        setFavorites((prev) =>
          prev.filter((f) => String(f.id || f.mediaId) !== String(mediaId))
        );
        toast.success("Removed from favorites");
      } else {
        await proxyApiService.createBookmark(playlistId, String(mediaId), "tv");
        const newFavorite = {
          id: mediaId,
          mediaId,
          name: movie.name,
          logo: movie.posterPath || movie.logoPath || "",
          mediaType: "tv",
          group: "Series",
        };

        setFavorites((prev) => [...prev, newFavorite]);
        toast.success("Added to favorites");
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorites");
    } finally {
      setProcessingFavorites((prev) => {
        const newSet = new Set(prev);

        newSet.delete(mediaId);

        return newSet;
      });
    }
  };

  const handleSearch = (value) => {
    setSearchQuery(value);

    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchSeries();
    }, 500);

    setSearchDebounce(timer);
  };

  const handlePageJump = () => {
    const page = parseInt(pageJumpValue);

    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setShowPageJump(false);
      setPageJumpValue("");
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages.map((page, idx) => {
      if (page === "...") {
        return (
          <button
            className="btn btn-sm btn-ghost"
            key={`ellipsis-${idx}`}
            onClick={() => setShowPageJump(true)}
          >
            ...
          </button>
        );
      }

      return (
        <button
          className={`btn btn-sm ${currentPage === page ? "btn-primary" : "btn-ghost"}`}
          key={page}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </button>
      );
    });
  };

  const gridClasses = {
    1: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    2: "grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
    3: "grid-cols-3 md:grid-cols-4 lg:grid-cols-5",
    4: "grid-cols-3 md:grid-cols-5 lg:grid-cols-7",
  };

  // Portrait poster sizes with 2:3 aspect ratio
  const posterSizes = {
    1: "aspect-[2/3] h-auto",
    2: "aspect-[2/3] h-auto",
    3: "aspect-[2/3] h-auto",
    4: "aspect-[2/3] h-auto",
  };

  return (
    <div
      className="min-h-screen bg-scroll md:bg-fixed"
      style={{
        backgroundImage: "url(/background.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white mb-4 px-4 py-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => navigate(`/playlist-manager/${playlistId}`)}
          >
            <i className="fas fa-arrow-left mr-2" />
            Back to Profiles
          </button>

          <div className="flex items-center justify-between mb-6">
            {/* Left side - Profile with name and email */}
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
                {profile.avatar ? (
                  <img
                    alt="Avatar"
                    className="w-full h-full rounded-full object-cover"
                    src={profile.avatar}
                  />
                ) : (
                  <i className="fas fa-user text-white text-2xl" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">
                  {profile.name || user?.name || "User"}
                </h1>
                <p className="text-sm text-gray-400 mt-1">
                  {domain || playlist?.domain || "Loading..."}
                </p>
              </div>
            </div>

            {/* Right side - LunaKey */}
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-end">
                <span className="text-xs sm:text-base text-white mb-1 sm:mb-2">
                  LunaKey™
                </span>
                <div className="flex items-center gap-2">
                  <code
                    className="px-2 sm:px-4 py-1 sm:py-2 bg-blue-600 text-white rounded-lg font-mono text-base sm:text-lg font-semibold cursor-pointer hover:bg-blue-700 transition-colors"
                    onClick={() => {
                      const code =
                        playlist?.code || playlist?.playlistCode || "";

                      navigator.clipboard.writeText(code);
                      toast.success("Copied!");
                    }}
                  >
                    {playlist?.code ||
                      playlist?.playlistCode ||
                      "XXXX-XXXX-XXXX"}
                  </code>
                  <button
                    className="p-2.5 hover:bg-gray-700 rounded-lg transition-colors hidden sm:block"
                    title="Copy LunaKey™"
                    onClick={() => {
                      const code =
                        playlist?.code || playlist?.playlistCode || "";

                      navigator.clipboard.writeText(code);
                      toast.success("LunaKey™ copied!");
                    }}
                  >
                    <i className="fas fa-copy text-gray-400 text-lg" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap sm:flex-nowrap gap-2 mb-6 p-2 sm:p-1.5 bg-gray-100 dark:bg-gray-700/50 rounded-3xl sm:rounded-full w-full sm:w-fit">
          {[
            { id: "overview", icon: "fas fa-th-large", label: "Overview" },
            { id: "channels", icon: "fas fa-tv", label: "Channels" },
            { id: "movies", icon: "fas fa-film", label: "Movies" },
            { id: "series", icon: "fas fa-video", label: "Series" },
            { id: "favorites", icon: "fas fa-star", label: "Favorites" },
            { id: "settings", icon: "fas fa-cog", label: "Settings" },
          ].map((tab) => (
            <button
              className={`px-4 sm:px-5 py-2.5 font-medium capitalize transition-all rounded-full w-[calc(50%-0.25rem)] sm:w-auto ${
                tab.id === "series"
                  ? "bg-white dark:bg-gray-800/30 text-blue-500 dark:text-blue-400 shadow-md transform sm:scale-105"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50"
              }`}
              key={tab.id}
              onClick={() =>
                navigate(`/playlist-manager/${playlistId}/manage?tab=${tab.id}`)
              }
            >
              <div className="flex items-center gap-2">
                <i className={`${tab.icon} text-sm`} />
                <span className="text-sm sm:text-base">{tab.label}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Category Header */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-white mb-2">
            {decodeURIComponent(categoryName)}
          </h2>
          <p className="text-gray-300">{totalItems.toLocaleString()} series</p>
        </div>

        {/* Controls */}
        <div className="card p-4 mb-6 shadow-md">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <input
                className="input pl-10 pr-4 w-full"
                placeholder="Search series..."
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
              <i className="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>

            {/* Sort */}
            <select
              className="select bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split("-");

                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
                setCurrentPage(1);
              }}
            >
              <option value="dateAdded-desc">Date Added ↓</option>
              <option value="dateAdded-asc">Date Added ↑</option>
              <option value="year-desc">Year ↓</option>
              <option value="year-asc">Year ↑</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="duration-desc">Duration ↓</option>
              <option value="duration-asc">Duration ↑</option>
              <option value="random-asc">Random</option>
              <option value="criticRating-desc">Critic Rating ↓</option>
              <option value="criticRating-asc">Critic Rating ↑</option>
              <option value="audienceRating-desc">Audience Rating ↓</option>
              <option value="audienceRating-asc">Audience Rating ↑</option>
              <option value="releaseDate-desc">Release Date ↓</option>
              <option value="releaseDate-asc">Release Date ↑</option>
            </select>

            {/* Items per page */}
            <select
              className="select bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-lg"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>

            {/* Grid size slider */}
            <div className="flex items-center gap-2">
              <i className="fas fa-th-large text-gray-600" />
              <input
                className="range range-sm w-24"
                max="4"
                min="1"
                type="range"
                value={gridSize}
                onChange={(e) => setGridSize(Number(e.target.value))}
              />
              <i className="fas fa-th text-gray-600" />
            </div>
          </div>
        </div>

        {/* Series Grid */}
        {loading ? (
          <div className="card p-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="text-gray-500 mt-4">Loading series...</p>
          </div>
        ) : series.length > 0 ? (
          <>
            <motion.div
              animate={{ opacity: 1 }}
              className={`grid ${gridClasses[gridSize]} gap-4 mb-6`}
              initial={{ opacity: 0 }}
            >
              {series.map((movie) => {
                const isFav = isFavorite(movie.mediaId);

                return (
                  <div
                    className="card overflow-hidden hover:shadow-xl transition-all group relative hover:scale-105 hover:border-2 hover:border-[#6495ED]"
                    key={movie.mediaId}
                  >
                    {/* Poster */}
                    <div
                      className={`relative ${posterSizes[gridSize]} bg-gray-200 dark:bg-gray-800`}
                    >
                      {movie.posterPath || movie.logoPath ? (
                        <img
                          alt={movie.name}
                          className="w-full h-full object-cover"
                          src={movie.posterPath || movie.logoPath}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="fas fa-film text-4xl text-gray-400" />
                        </div>
                      )}

                      {/* Favorite button overlay */}
                      <button
                        className={`absolute top-2 right-2 p-2 rounded-full bg-black/50 backdrop-blur-sm transition-all ${
                          processingFavorites.has(movie.mediaId)
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-black/70"
                        }`}
                        disabled={processingFavorites.has(movie.mediaId)}
                        onClick={() => handleToggleFavorite(movie)}
                      >
                        {processingFavorites.has(movie.mediaId) ? (
                          <i className="fas fa-spinner fa-spin text-white" />
                        ) : (
                          <i
                            className={`${isFav ? "fas" : "far"} fa-star ${
                              isFav ? "text-yellow-400" : "text-white"
                            }`}
                          />
                        )}
                      </button>
                    </div>

                    {/* Movie info */}
                    <div className="p-3">
                      <h3 className="font-semibold text-sm line-clamp-2 mb-1">
                        {movie.name}
                      </h3>
                      {movie.year && (
                        <p className="text-xs text-gray-500">{movie.year}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </motion.div>

            {/* Pagination */}
            <div className="card p-4 shadow-md">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                  {totalItems.toLocaleString()} series
                </p>

                <div className="flex items-center gap-2">
                  <button
                    className="btn btn-sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className="fas fa-chevron-left" />
                  </button>

                  {renderPagination()}

                  <button
                    className="btn btn-sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className="fas fa-chevron-right" />
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="card p-12 text-center">
            <i className="fas fa-film text-6xl text-gray-300 mb-4" />
            <p className="text-gray-500">No series found</p>
          </div>
        )}
      </div>

      {/* Page Jump Modal */}
      {showPageJump && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Jump to Page</h3>
            <input
              className="input w-full mb-4"
              max={totalPages}
              min="1"
              placeholder={`Enter page (1-${totalPages})`}
              type="number"
              value={pageJumpValue}
              onChange={(e) => setPageJumpValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handlePageJump()}
            />
            <div className="flex gap-2">
              <button className="btn flex-1" onClick={handlePageJump}>
                Go
              </button>
              <button
                className="btn btn-ghost flex-1"
                onClick={() => {
                  setShowPageJump(false);
                  setPageJumpValue("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeriesCategoryView;
